import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const toForm = (c) => ({
  name: c?.name || '',
  email: c?.email || '',
  phone: c?.phone || '',
  age: c?.age ?? '',
  segment: c?.segment || '',
  calendar_names: (c?.calendar_names || []).join(', '),
  session_price: c?.session_price ?? '',
  weekly_report: c?.weekly_report == null ? 'auto' : c.weekly_report ? 'on' : 'off',
  test_account: !!c?.test_account,
});
const REPORT_OPTIONS = [['auto', 'Auto'], ['on', 'Włączony'], ['off', 'Wyłączony']];

// Trainer edits a client's basic data, including how the client appears in
// Google Calendar titles (so synced trainings are matched to them) and the
// price of a single training for pay-per-session clients.
export const ClientEditDialog = ({ client, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState(toForm(client));
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(toForm(client)); }, [open, client]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      calendar_names: form.calendar_names.split(',').map((n) => n.trim()).filter(Boolean),
      weekly_report: form.weekly_report === 'auto' ? null : form.weekly_report === 'on',
      test_account: form.test_account,
    };
    if (form.email.trim()) payload.email = form.email.trim();
    if (form.phone.trim()) payload.phone = form.phone.trim();
    if (form.age !== '') payload.age = parseInt(form.age, 10);
    if (form.segment) payload.segment = form.segment;
    if (form.session_price !== '') payload.session_price = parseFloat(String(form.session_price).replace(',', '.'));
    try {
      const res = await axios.put(`${API_URL}/api/clients/${client.id}`, payload, { withCredentials: true });
      toast.success('Zapisano dane klienta');
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving client:', error);
      toast.error(error.response?.data?.detail || 'Nie udało się zapisać danych klienta');
    } finally {
      setSaving(false);
    }
  };

  const field = 'bg-[var(--bg-primary)] border-[var(--border-soft)] rounded-2xl';
  const label = 'label-text text-[var(--text-muted)] mb-2 block';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--bg-card)] border-[var(--border-soft)] rounded-3xl max-w-md max-h-[85vh] overflow-y-auto" data-testid="client-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-extrabold tracking-tight">Dane klienta</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 mt-2">
          <div>
            <Label className={label}>Imię i nazwisko</Label>
            <Input value={form.name} onChange={set('name')} className={field} required data-testid="edit-client-name" />
          </div>
          <div>
            <Label className={label}>Nazwy w kalendarzu</Label>
            <Input value={form.calendar_names} onChange={set('calendar_names')} className={field}
              placeholder="np. Ula, u Jurka" data-testid="edit-client-calendar-names" />
            <p className="text-xs text-[var(--text-muted)] mt-1.5">
              Jak wpisujesz tę osobę w tytule w Google Calendar (po przecinku). Treningi z tymi nazwami trafią do tego klienta.
            </p>
          </div>
          <div>
            <Label className={label}>Cena pojedynczego treningu (zł)</Label>
            <Input inputMode="decimal" value={form.session_price} onChange={set('session_price')} className={field}
              placeholder="dla klientów bez pakietu" data-testid="edit-client-session-price" />
          </div>
          <div>
            <Label className={label}>E-mail</Label>
            <Input type="email" value={form.email} onChange={set('email')} className={field}
              placeholder="potrzebny do założenia konta klienta" data-testid="edit-client-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className={label}>Telefon</Label>
              <Input value={form.phone} onChange={set('phone')} className={field} />
            </div>
            <div>
              <Label className={label}>Wiek</Label>
              <Input type="number" value={form.age} onChange={set('age')} className={field} />
            </div>
          </div>
          <div>
            <Label className={label}>Raport tygodniowy</Label>
            <div className="grid grid-cols-3 gap-2" data-testid="edit-client-weekly-report">
              {REPORT_OPTIONS.map(([v, l]) => (
                <button key={v} type="button" onClick={() => setForm((f) => ({ ...f, weekly_report: v }))} aria-pressed={form.weekly_report === v}
                  className={`h-10 rounded-full text-xs font-semibold ${form.weekly_report === v ? 'bg-[var(--cta-bg)] text-[var(--cta-fg)]' : 'glass-soft'}`}>{l}</button>
              ))}
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-1.5">Auto = raport dla klientów z aktywnym pakietem „Prowadzenie online”.</p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="edit-client-test-account">
            <input type="checkbox" checked={form.test_account} onChange={(e) => setForm((f) => ({ ...f, test_account: e.target.checked }))}
              className="mt-1 w-4 h-4 accent-[#C9A84C]" />
            <span>
              <span className="block text-sm font-semibold">Konto testowe</span>
              <span className="block text-xs text-[var(--text-muted)]">Nie liczy się w statystykach pulpitu (frekwencja, obciążenia, waga, alerty).</span>
            </span>
          </label>
          <button type="submit" disabled={saving} className="cta-primary w-full h-12 rounded-full text-xs font-semibold tracking-[0.2em] uppercase" data-testid="edit-client-save">
            {saving ? 'Zapisywanie…' : 'Zapisz'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
